(() => {
  const frameValues = new Set(['black', 'white', 'oak']);
  const canonicalFinishValues = ['matte-white', 'natural-oak', 'matte-black'];

  class LtpCanonicalPdp {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.mediaGallery = productInfo.querySelector('media-gallery[data-ltp-canonical-gallery]');
      this.lastFrameFinish = null;
      this.editorialPdp = productInfo.dataset.ltpEditorialPdp === 'true';
      this.contextualGalleryActive = this.editorialPdp;
      this.previousGalleryPosition = this.editorialPdp
        ? Number(this.mediaGallery?.querySelector('[data-media-id].is-active')?.dataset.ltpGalleryPosition || 1)
        : 1;
      this.variantImagePreloads = new Map();
      this.heroRenderToken = 0;
      this.onOptionChange = this.onOptionChange.bind(this);
      this.onOptionIntent = this.onOptionIntent.bind(this);
      this.onGalleryPositionChange = this.onGalleryPositionChange.bind(this);
      this.onGalleryNavigationIntent = this.onGalleryNavigationIntent.bind(this);
      this.onHeroPointerDown = this.onHeroPointerDown.bind(this);
      this.onHeroPointerEnd = this.onHeroPointerEnd.bind(this);
      this.onGalleryTouchStart = this.onGalleryTouchStart.bind(this);
      this.onGalleryTouchEnd = this.onGalleryTouchEnd.bind(this);
      this.productInfo.addEventListener('change', this.onOptionChange, true);
      this.productInfo.addEventListener('pointerover', this.onOptionIntent, { passive: true });
      this.productInfo.addEventListener('pointerdown', this.onOptionIntent, { passive: true });
      this.productInfo.addEventListener('focusin', this.onOptionIntent);
      this.mediaGallery?.addEventListener('ltp:gallery-position-change', this.onGalleryPositionChange);
      this.mediaGallery?.addEventListener('click', this.onGalleryNavigationIntent, true);
      const selectedVariantHero = this.mediaGallery?.querySelector('[data-ltp-selected-variant-hero]');
      selectedVariantHero?.addEventListener('pointerdown', this.onHeroPointerDown);
      selectedVariantHero?.addEventListener('pointerup', this.onHeroPointerEnd);
      selectedVariantHero?.addEventListener('pointercancel', this.onHeroPointerEnd);
      const gallerySlider = this.mediaGallery?.elements?.viewer?.slider;
      gallerySlider?.addEventListener('touchstart', this.onGalleryTouchStart, { passive: true });
      gallerySlider?.addEventListener('touchend', this.onGalleryTouchEnd, { passive: true });
      gallerySlider?.addEventListener('touchcancel', () => { this.galleryEdgeTouch = null; }, { passive: true });
      gallerySlider?.addEventListener('touchstart', () => {
        if (this.mobileEditorialGallery) this.mobilePresentationToken = (this.mobilePresentationToken || 0) + 1;
      }, { passive: true });
      if (this.editorialPdp) this.adoptInitialEditorialImage();
      const mobileLifestyleOpening = this.mediaGallery?.dataset.ltpMobileOpening === 'lifestyle' &&
        !this.mediaGallery.mql.matches;
      this.refreshSelectorState({
        showVariantHero: !mobileLifestyleOpening && (this.selectors?.dataset.ltpInitiallyUnselected === 'false' ||
          Boolean(this.selectors?.querySelector('[data-ltp-size-value]:checked') || this.getSelectedFormat())),
        deferPredictions: true,
      });
      if (this.mobileEditorialGallery && mobileLifestyleOpening) this.prepareMobileContext(this.getVariantState(), false);
      this.observeBlankwallButton();
      this.prewarmInitialFormats();
    }

    get selectors() {
      return this.productInfo.querySelector('variant-selects[data-ltp-canonical-selectors]');
    }

    get mobileEditorialGallery() {
      return this.editorialPdp && this.mediaGallery?.ltpMobileLoop;
    }

    prepareMobileContext(state, present) {
      if (!state || !this.mobileEditorialGallery) return;
      const gallery = this.mediaGallery;
      const key = state.dataset.ltpVariantState;
      const token = this.mobileContextToken = (this.mobileContextToken || 0) + 1;
      const presentationToken = this.mobilePresentationToken || 0;
      if (present) delete gallery.dataset.ltpMobileOpening;
      const prepare = () => {
        if (token !== this.mobileContextToken || key !== this.getSelectedVariantKey()) return;
        this.supersedeVariantWork(key);
        const record = this.prepareVariantKey(key, 'high');
        if (!record) return;
        record.visited = true;
        gallery.ltpSelectedImageReady = record.ready;
        const ready = () => {
          if (token !== this.mobileContextToken || key !== this.getSelectedVariantKey() || !record.decoded) return;
          const printOnly = this.getSelectedFormat()?.dataset.ltpFormatValue === 'print-only';
          const position = printOnly ? 1 : canonicalFinishValues.indexOf(key.split('__')[1]) + 1;
          this.updateContextualSlide(position, key, true);
          let changed = false;
          [2, 3].forEach((number) => {
            const slide = gallery.querySelector(`[data-ltp-fixed-gallery] [data-ltp-gallery-position="${number}"]`);
            const thumbnail = gallery.elements.thumbnails?.querySelector(`[data-ltp-gallery-position="${number}"]`);
            if (slide) {
              changed ||= slide.hidden !== printOnly;
              slide.hidden = printOnly;
              slide.style.display = printOnly ? 'none' : '';
            }
            if (thumbnail) {
              thumbnail.hidden = printOnly;
              thumbnail.style.display = printOnly ? 'none' : '';
            }
          });
          gallery.dataset.ltpSlideCount = printOnly ? '2' : '4';
          gallery.setMobileIndicatorCount?.(printOnly);
          if (present && presentationToken === (this.mobilePresentationToken || 0)) {
            this.settingGalleryPosition = true;
            const slide = gallery.querySelector(`[data-ltp-fixed-gallery] [data-ltp-gallery-position="${position}"]`);
            gallery.setActiveMedia(slide.dataset.mediaId, false, 0, true);
            this.settingGalleryPosition = false;
            this.previousGalleryPosition = position;
          }
          if (changed) gallery.refreshMobileLoop();
          this.exitSelectedVariantHero();
          this.getContextVariantKeys().forEach((contextKey, index) => {
            if (contextKey === key) return;
            const sibling = this.prepareVariantKey(contextKey, 'low');
            sibling?.ready.then(() => {
              if (sibling.decoded && this.getContextVariantKeys().includes(contextKey)) {
                this.updateContextualSlide(index + 1, contextKey, false);
              }
            });
          });
        };
        if (record.decoded) ready();
        else record.ready.then(ready);
      };
      if (present || gallery.ltpOpeningSettled) prepare();
      else gallery.ltpOpeningReady.then(prepare);
    }

    adoptInitialEditorialImage() {
      const hero = this.mediaGallery?.querySelector('[data-ltp-selected-variant-hero]');
      const image = hero?.querySelector('img');
      const key = hero?.dataset.ltpVariantKey;
      if (!image || !key) return;
      const sourceImage = this.getVariantState(key)?.content.querySelector('[data-ltp-selected-hero-fragment] img');
      const record = {
        image,
        sourceImage,
        viewportWidth: window.innerWidth,
        pixelRatio: window.devicePixelRatio || 1,
        decoded: false,
        visited: true,
        cancelled: false,
        ready: Promise.resolve(this.mediaGallery.ltpOpeningReady)
          .then(() => this.mediaGallery.prepareMediaImage(image, 'high'))
          .then(() => { record.decoded = Boolean(image.naturalWidth); }),
        cancel: () => {
          if (image.isConnected) return;
          record.cancelled = true;
          image.removeAttribute('srcset');
          image.removeAttribute('src');
        },
      };
      this.variantImagePreloads.set(key, record);
      this.mediaGallery.ltpSelectedImageReady = record.ready;
    }

    onOptionChange(event) {
      const input = event.target;
      if (!input?.matches?.('input[type="radio"]') || !this.selectors?.contains(input)) return;

      event.stopPropagation();
      if (this.editorialPdp) this.mediaGallery?.releaseEditorialOpening?.();
      this.contextWarmToken = (this.contextWarmToken || 0) + 1;
      if (this.mediaGallery) {
        this.mediaGallery.navigationToken = (this.mediaGallery.navigationToken || 0) + 1;
      }

      if (input.matches('[data-ltp-frame-finish-value]')) {
        this.lastFrameFinish = input.dataset.ltpFrameFinishValue;
      }

      this.resolveMissingSelections();
      this.mediaGallery?.setMobileIndicatorCount?.(
        this.getSelectedFormat()?.dataset.ltpFormatValue === 'print-only'
      );
      this.setFrameFinishVisibility();
      this.setSizeAvailability();
      this.updateSelectedLabels();
      this.syncBlankwallDimensions();
      this.contextualGalleryActive = Boolean(this.getSelectedVariantKey());
      if (this.contextualGalleryActive) this.positionContextualGallery();
      this.applySelectedVariant({ showVariantHero: true });
    }

    onOptionIntent(event) {
      const input = this.getIntentInput(event.target);
      if (!input || input.disabled) return;
      if (this.editorialPdp && event.type !== 'pointerover') this.mediaGallery?.releaseEditorialOpening?.();

      const key = this.getIntentVariantKey(input);
      if (!key) return;

      if (event.type === 'pointerdown') this.supersedeVariantWork(key);
      this.prepareVariantKey(key, event.type === 'pointerdown' ? 'high' : 'low');
    }

    getIntentInput(target) {
      const directInput = target?.closest?.('input[type="radio"]');
      if (directInput && this.selectors?.contains(directInput)) return directInput;

      const label = target?.closest?.('label[for]');
      if (!label) return null;
      const input = document.getElementById(label.htmlFor);
      return input && this.selectors?.contains(input) ? input : null;
    }

    getIntentVariantKey(input) {
      const selectedSize = this.selectors?.querySelector('[data-ltp-size-value]:checked')?.dataset.ltpSizeValue;
      const selectedFormat = this.getSelectedFormat();

      if (input.matches('[data-ltp-size-value]')) {
        const format = selectedFormat || this.selectors?.querySelector('[data-ltp-format-value="classic-frame"]');
        return this.buildVariantKey(input.dataset.ltpSizeValue, format);
      }

      if (input.matches('[data-ltp-format-value]')) {
        return this.buildVariantKey(selectedSize || 'small', input);
      }

      if (input.matches('[data-ltp-frame-finish-value]')) {
        return this.buildVariantKey(selectedSize || 'small', selectedFormat, input.dataset.ltpFrameFinishValue);
      }

      return '';
    }

    ensureFrameFinish() {
      const selectedFinish = this.selectors?.querySelector('[data-ltp-frame-finish-value]:checked');
      if (selectedFinish) return selectedFinish;

      const preferredFinish = this.lastFrameFinish || 'matte-white';
      const preferredInput = this.selectors?.querySelector(
        `[data-ltp-frame-finish-value="${preferredFinish}"]`
      );
      const fallbackInput = this.selectors?.querySelector('[data-ltp-frame-finish-value="matte-white"]');
      const nextFinish = preferredInput && !preferredInput.classList.contains('disabled') ? preferredInput : fallbackInput;
      if (nextFinish) nextFinish.checked = true;
      return nextFinish;
    }

    resolveMissingSelections() {
      const selectors = this.selectors;
      if (!selectors) return;

      let size = selectors.querySelector('[data-ltp-size-value]:checked');
      let format = this.getSelectedFormat();
      if (size && !format) {
        format = selectors.querySelector('[data-ltp-format-value="classic-frame"]');
        if (format && !format.disabled) format.checked = true;
      }
      if (format && !size) {
        size = selectors.querySelector('[data-ltp-size-value="small"]');
        if (size && !size.disabled) size.checked = true;
      }
      if (format && size && format.dataset.ltpFormatValue !== 'print-only') this.ensureFrameFinish();
      if (format || size) selectors.dataset.ltpInitiallyUnselected = 'false';
    }

    setFrameFinishVisibility(format = this.getSelectedFormat()) {
      const fieldset = this.selectors?.querySelector('[data-ltp-frame-finish-fieldset]');
      if (!fieldset) return;
      fieldset.hidden = !format || format.dataset.ltpFormatValue === 'print-only';
    }

    setSizeAvailability() {
      const fieldset = this.selectors?.querySelector('[data-ltp-size-fieldset]');
      if (!fieldset) return;

      fieldset.dataset.ltpInactive = 'false';
      fieldset.setAttribute('aria-disabled', 'false');
      fieldset.querySelectorAll('[data-ltp-size-value]').forEach((input) => {
        input.disabled = input.classList.contains('disabled');
      });
    }

    getSelectedFormat() {
      return this.selectors?.querySelector('[data-ltp-format-value]:checked') || null;
    }

    getPreferredFinishValue() {
      return (
        this.selectors?.querySelector('[data-ltp-frame-finish-value]:checked')?.dataset.ltpFrameFinishValue ||
        this.lastFrameFinish ||
        'matte-white'
      );
    }

    buildVariantKey(size, format, finish = null) {
      if (!size || !format) return '';
      const printOnly = format.dataset.ltpFormatValue === 'print-only';
      const colour = printOnly ? 'no-frame' : finish || this.getPreferredFinishValue();
      const frameType = format.dataset.ltpBackendFrameType;
      return colour && frameType ? `${size}__${colour}__${frameType}` : '';
    }

    getContextVariantKeys() {
      const size = this.selectors?.querySelector('[data-ltp-size-value]:checked')?.dataset.ltpSizeValue;
      const format = this.getSelectedFormat();
      if (!size || !format) return [];

      if (format.dataset.ltpFormatValue === 'print-only') {
        return [this.buildVariantKey(size, format)];
      }

      return canonicalFinishValues.map((finish) => this.buildVariantKey(size, format, finish)).filter(Boolean);
    }

    updateSelectedLabels() {
      const selectors = this.selectors;
      if (!selectors) return;

      const size = selectors.querySelector('[data-ltp-size-value]:checked');
      this.updateLegend(selectors.querySelector('[data-ltp-size-fieldset]'), size?.value || '');

      const format = this.getSelectedFormat();
      this.updateLegend(selectors.querySelector('[data-ltp-format-fieldset]'), format?.value || '');

      const finish = selectors.querySelector('[data-ltp-frame-finish-value]:checked');
      this.updateLegend(selectors.querySelector('[data-ltp-frame-finish-fieldset]'), finish?.value || '');
    }

    updateLegend(fieldset, value) {
      if (!fieldset) return;
      const label = fieldset.querySelector('[data-selected-value]');
      const separator = fieldset.querySelector('[data-selected-separator]');
      if (label) label.textContent = value;
      if (separator) separator.hidden = !value;
    }

    syncBlankwallDimensions() {
      if (this.productInfo.dataset.ltpV2ProductTemplate !== 'true') return;

      const dimensionSource = this.productInfo.querySelector('[data-ltp-blankwall-dimensions]');
      if (!dimensionSource) return;

      const selectedSize = this.selectors?.querySelector('[data-ltp-size-value]:checked')?.dataset.ltpSizeValue || '';
      const dimensions = selectedSize ? dimensionSource.dataset[selectedSize] || '' : '';
      dimensionSource.textContent = dimensions;
      this.productInfo.dataset.ltpBlankwallSizeSelected = dimensions ? 'true' : 'false';

      const customSource = this.productInfo.querySelector('#ar-dimensions-custom-source-id');
      if (customSource) customSource.textContent = `#${dimensionSource.id}`;

      const blankwallButton = this.productInfo.querySelector('#blankwall-container #ar-button');
      if (blankwallButton) {
        blankwallButton.disabled = !dimensions;
        blankwallButton.setAttribute('aria-disabled', String(!dimensions));
      }
      this.syncBlankwallHelperState();
    }

    observeBlankwallButton() {
      if (this.productInfo.dataset.ltpV2ProductTemplate !== 'true') return;

      const attach = () => {
        const button = this.productInfo.querySelector('#blankwall-container #ar-button');
        if (!button) return;
        this.blankwallInsertionObserver?.disconnect();
        this.blankwallButtonObserver = new MutationObserver(() => this.syncBlankwallHelperState());
        this.blankwallButtonObserver.observe(button, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class', 'style'],
        });
        this.syncBlankwallHelperState();
      };

      if (this.productInfo.querySelector('#blankwall-container #ar-button')) {
        attach();
      } else {
        this.blankwallInsertionObserver = new MutationObserver(attach);
        this.blankwallInsertionObserver.observe(this.productInfo, { childList: true, subtree: true });
      }
    }

    syncBlankwallHelperState() {
      const helper = this.productInfo.querySelector('[data-ltp-blankwall-helper]');
      if (!helper) return;

      const button = this.productInfo.querySelector('#blankwall-container #ar-button');
      const enabled = Boolean(
        button &&
        !button.disabled &&
        button.getAttribute('aria-disabled') !== 'true' &&
        getComputedStyle(button).pointerEvents !== 'none'
      );
      helper.hidden = enabled;
      if (!button) return;

      const descriptionIds = (button.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== helper.id);
      if (!enabled) descriptionIds.push(helper.id);
      if (descriptionIds.length) {
        button.setAttribute('aria-describedby', descriptionIds.join(' '));
      } else {
        button.removeAttribute('aria-describedby');
      }
    }

    getSelectedVariantKey() {
      const selectors = this.selectors;
      if (!selectors) return '';

      const size = selectors.querySelector('[data-ltp-size-value]:checked')?.dataset.ltpSizeValue;
      const format = this.getSelectedFormat();
      const printOnly = format?.dataset.ltpFormatValue === 'print-only';
      const colour = printOnly
        ? 'no-frame'
        : selectors.querySelector('[data-ltp-frame-finish-value]:checked')?.dataset.ltpFrameFinishValue;
      const frameType = format?.dataset.ltpBackendFrameType;
      return size && colour && frameType ? `${size}__${colour}__${frameType}` : '';
    }

    getVariantState(key = this.getSelectedVariantKey()) {
      if (!key) return null;
      return this.productInfo.querySelector(`template[data-ltp-variant-state="${key}"]`);
    }

    getFormatState() {
      const formatValue = this.getSelectedFormat()?.dataset.ltpFormatValue;
      if (!formatValue) return null;
      return this.productInfo.querySelector(`template[data-ltp-format-state="${formatValue}"]`);
    }

    getFormatPreviewState() {
      const format = this.getSelectedFormat();
      const size = this.selectors?.querySelector('[data-ltp-size-value]:checked')?.dataset.ltpSizeValue;
      if (!format || !size) return null;

      const printOnly = format.dataset.ltpFormatValue === 'print-only';
      const colour = printOnly
        ? 'no-frame'
        : this.selectors?.querySelector('[data-ltp-frame-finish-value]:checked')?.dataset.ltpFrameFinishValue;
      const frameType = format.dataset.ltpBackendFrameType;
      if (!colour || !frameType) return null;
      return this.getVariantState(`${size}__${colour}__${frameType}`);
    }

    applySelectedVariant({ showVariantHero }) {
      const state = this.getVariantState();
      if (!state) {
        this.setIncompleteState({
          priceState: this.getFormatState(),
          heroState: this.getFormatPreviewState(),
          showVariantHero,
        });
        return;
      }

      if (showVariantHero) {
        this.showSelectedVariantHero(state);
      } else {
        this.exitSelectedVariantHero();
      }
      this.updateProductState(state);
      this.prewarmSelectedContext();
    }

    showSelectedVariantHero(state) {
      if (this.mobileEditorialGallery) {
        this.prepareMobileContext(state, true);
        return;
      }
      if (this.mediaGallery) delete this.mediaGallery.dataset.ltpMobileOpening;
      const hero = this.mediaGallery?.querySelector('[data-ltp-selected-variant-hero]');
      const heroMedia = hero?.querySelector('[data-ltp-selected-variant-hero-media]');
      const mediaFragment = state.content.querySelector('[data-ltp-selected-hero-fragment]');
      if (!hero || !heroMedia || !mediaFragment?.firstElementChild) {
        this.exitSelectedVariantHero();
        return;
      }

      const variantKey = state.dataset.ltpVariantState;
      this.requestedHeroKey = variantKey;
      this.supersedeVariantWork(variantKey);
      if (hero.dataset.ltpVariantKey === variantKey && heroMedia.firstElementChild) {
        this.heroRenderToken += 1;
        hero.hidden = false;
        hero.setAttribute('aria-hidden', 'false');
        this.mediaGallery?.querySelector('[data-ltp-fixed-gallery]')?.setAttribute('aria-hidden', 'true');
        return;
      }

      const record = this.prepareVariantState(state, 'high');
      if (!record) {
        if (!this.contextualGalleryActive) this.exitSelectedVariantHero();
        return;
      }
      record.visited = true;

      const renderToken = ++this.heroRenderToken;
      this.mediaGallery.ltpSelectedImageReady = record.ready;
      this.mediaGallery.scheduleCanonicalImages?.();
      const present = () => {
        if (renderToken !== this.heroRenderToken || record.cancelled || !record.image.naturalWidth) return;

        const nextMedia = mediaFragment.cloneNode(true);
        const placeholder = nextMedia.querySelector('img');
        if (!placeholder) return;
        placeholder.replaceWith(record.image);
        heroMedia.replaceChildren(...nextMedia.childNodes);
        hero.dataset.ltpVariantKey = variantKey;
        hero.hidden = false;
        hero.setAttribute('aria-hidden', 'false');
        this.mediaGallery?.querySelector('[data-ltp-fixed-gallery]')?.setAttribute('aria-hidden', 'true');
      };
      if (record.decoded && record.image.complete) present();
      else record.ready.then(present);
    }

    exitSelectedVariantHero() {
      this.heroRenderToken += 1;
      const hero = this.mediaGallery?.querySelector('[data-ltp-selected-variant-hero]');
      if (!hero || hero.hidden) return;
      hero.hidden = true;
      hero.setAttribute('aria-hidden', 'true');
      this.mediaGallery?.querySelector('[data-ltp-fixed-gallery]')?.removeAttribute('aria-hidden');
    }

    getHeroImageSizes(sourceImage) {
      const heroMedia = this.mediaGallery?.querySelector('[data-ltp-selected-variant-hero-media]');
      const width = Math.ceil(
        heroMedia?.getBoundingClientRect().width ||
        this.mediaGallery?.getBoundingClientRect().width ||
        window.innerWidth ||
        0
      );
      return width > 0 ? `${width}px` : sourceImage.getAttribute('sizes') || '100vw';
    }

    prepareVariantState(state, priority = 'low') {
      return this.prepareVariantKey(state?.dataset.ltpVariantState, priority);
    }

    prepareVariantKey(key, priority = 'low') {
      if (!key) return null;

      let existing = this.variantImagePreloads.get(key);
      if (existing && (existing.viewportWidth !== window.innerWidth ||
          existing.pixelRatio !== (window.devicePixelRatio || 1))) {
        existing.cancel();
        this.variantImagePreloads.delete(key);
        existing = null;
      }
      if (existing) {
        if (priority === 'high') {
          this.variantImagePreloads.delete(key);
          this.variantImagePreloads.set(key, existing);
          existing.image.fetchPriority = 'high';
        }
        return existing;
      }

      const selected = this.variantImagePreloads.get(this.priorityVariantKey);
      if (priority !== 'high' && selected && !selected.decoded) return null;

      const state = this.getVariantState(key);
      const sourceImage = state?.content.querySelector('[data-ltp-selected-hero-fragment] img');
      if (!sourceImage) return null;

      const removable = [...this.variantImagePreloads].filter(([cachedKey, cachedRecord]) =>
        !cachedRecord.image.isConnected && cachedKey !== this.requestedHeroKey && cachedKey !== this.priorityVariantKey
      );
      const speculative = removable.filter(([, record]) => !record.visited);
      while (speculative.length && (this.variantImagePreloads.size >= 24 || speculative.length >= 4)) {
        const [cachedKey, cachedRecord] = speculative.shift();
        cachedRecord.cancel();
        this.variantImagePreloads.delete(cachedKey);
      }
      if (this.variantImagePreloads.size >= 24) {
        if (priority !== 'high') return null;
        const oldest = removable.find(([cachedKey]) => this.variantImagePreloads.has(cachedKey));
        if (!oldest) return null;
        oldest[1].cancel();
        this.variantImagePreloads.delete(oldest[0]);
      }

      const image = new Image();
      Array.from(sourceImage.attributes).forEach(({ name, value }) => {
        if (!['src', 'srcset', 'sizes', 'loading', 'fetchpriority', 'decoding'].includes(name)) {
          image.setAttribute(name, value);
        }
      });
      image.loading = 'eager';
      image.decoding = 'async';
      image.fetchPriority = priority;
      image.sizes = this.getHeroImageSizes(sourceImage);

      let settleLoad;
      let loadSettled = false;
      const loadReady = new Promise((resolve) => {
        settleLoad = () => {
          if (loadSettled) return;
          loadSettled = true;
          image.removeEventListener('load', settleLoad);
          image.removeEventListener('error', settleLoad);
          resolve();
        };
        image.addEventListener('load', settleLoad, { once: true });
        image.addEventListener('error', settleLoad, { once: true });
      });

      const record = {
        image,
        sourceImage,
        viewportWidth: window.innerWidth,
        pixelRatio: window.devicePixelRatio || 1,
        decoded: false,
        visited: false,
        cancelled: false,
        ready: loadReady.then(() => {
          if (record.cancelled || !image.naturalWidth || typeof image.decode !== 'function') return undefined;
          return image.decode().catch(() => undefined);
        }).then(() => {
          record.decoded = !record.cancelled && Boolean(image.naturalWidth);
        }),
        cancel: () => {
          if (image.isConnected) return;
          record.cancelled = true;
          image.fetchPriority = 'low';
          settleLoad();
          image.removeAttribute('srcset');
          image.removeAttribute('src');
        },
      };

      this.variantImagePreloads.set(key, record);
      const sourceSet = sourceImage.getAttribute('srcset');
      if (sourceSet) image.srcset = sourceSet;
      image.src = sourceImage.getAttribute('src') || '';
      if (image.complete) settleLoad();
      return record;
    }

    supersedeVariantWork(priorityKey) {
      this.priorityVariantKey = priorityKey;
      this.contextWarmToken = (this.contextWarmToken || 0) + 1;
      const [selectedSize, , selectedFormat] = priorityKey.split('__');
      this.variantImagePreloads.forEach((record, key) => {
        if (key === priorityKey) return;
        record.image.fetchPriority = 'low';
        if (record.decoded || record.image.isConnected) return;
        const [size, , format] = key.split('__');
        if (size === selectedSize && format === selectedFormat && canonicalFinishValues.some((finish) =>
          key === `${size}__${finish}__${format}`
        )) return;
        record.cancel();
        this.variantImagePreloads.delete(key);
      });
    }

    prewarmSelectedContext() {
      if (this.editorialPdp && !this.mediaGallery?.ltpOpeningSettled) {
        if (!this.editorialWarmScheduled) {
          this.editorialWarmScheduled = true;
          this.mediaGallery.ltpOpeningReady.then(() => this.prewarmSelectedContext());
        }
        return;
      }
      const contextKeys = this.getContextVariantKeys();
      if (!contextKeys.length) return;

      const selectedKey = this.getSelectedVariantKey();
      const warmToken = (this.contextWarmToken || 0) + 1;
      this.contextWarmToken = warmToken;
      const selectedRecord = this.prepareVariantKey(selectedKey, 'high');
      selectedRecord?.ready.then(() => {
        if (warmToken !== this.contextWarmToken || selectedKey !== this.getSelectedVariantKey()) return;
        contextKeys.filter((key) => key !== selectedKey).forEach((key) => this.prepareVariantKey(key, 'low'));
      });
    }

    prewarmInitialFormats() {
      if (this.editorialPdp) return;
      const opening = this.mediaGallery?.querySelector('[data-ltp-gallery-position="1"] img');
      if (!opening) return;
      this.mediaGallery.prepareMediaImage(opening, 'high').then(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (this.getSelectedFormat()) return;
          this.selectors?.querySelectorAll('[data-ltp-format-value]').forEach((input) => {
            if (input.dataset.ltpFormatValue === 'classic-frame') return;
            this.prepareVariantKey(this.buildVariantKey('small', input), 'low');
          });
        }));
      });
    }

    onGalleryNavigationIntent(event) {
      const mobileButton = event.target.closest('.slider-button[name]');
      if (this.contextualGalleryActive && mobileButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.mediaGallery?.navigateMedia(mobileButton.name === 'previous' ? 'previous' : 'next');
        return;
      }
      if (!this.contextualGalleryActive && event.target.closest('[data-gallery-direction]')) {
        this.heroRenderToken += 1;
      }
    }

    onHeroPointerDown(event) {
      if (this.mobileEditorialGallery) return;
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.heroPointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      this.mediaGallery?.prepareNavigationTarget('next', 'low');
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }

    onHeroPointerEnd(event) {
      const start = this.heroPointerStart;
      this.heroPointerStart = null;
      if (!start || start.id !== event.pointerId || event.type === 'pointercancel') return;

      const horizontalDistance = event.clientX - start.x;
      const verticalDistance = event.clientY - start.y;
      if (Math.abs(horizontalDistance) < 36 || Math.abs(horizontalDistance) <= Math.abs(verticalDistance)) return;

      if (!this.contextualGalleryActive) this.heroRenderToken += 1;
      this.mediaGallery?.navigateMedia(horizontalDistance > 0 ? 'previous' : 'next');
    }

    onGalleryTouchStart(event) {
      if (this.mobileEditorialGallery) return;
      if (!this.contextualGalleryActive || !this.mediaGallery ||
          !this.mediaGallery.querySelector('[data-ltp-selected-variant-hero]')?.hidden) return;
      const touch = event.touches[0];
      const targets = this.mediaGallery.getAvailableMediaTargets();
      const active = this.mediaGallery.elements.viewer.querySelector('[data-media-id].is-active');
      const atFirst = active?.dataset.mediaId === targets[0]?.dataset.target;
      const atLast = active?.dataset.mediaId === targets[targets.length - 1]?.dataset.target;
      if (!touch || (!atFirst && !atLast)) return;
      this.galleryEdgeTouch = { x: touch.clientX, y: touch.clientY, atFirst, atLast };
    }

    onGalleryTouchEnd(event) {
      const start = this.galleryEdgeTouch;
      this.galleryEdgeTouch = null;
      const touch = event.changedTouches[0];
      if (!start || !touch || !this.contextualGalleryActive) return;
      const horizontal = touch.clientX - start.x;
      if (Math.abs(horizontal) < 36 || Math.abs(horizontal) <= Math.abs(touch.clientY - start.y)) return;
      if (start.atFirst && horizontal > 0) this.mediaGallery?.navigateMedia('previous');
      if (start.atLast && horizontal < 0) this.mediaGallery?.navigateMedia('next');
    }

    holdCurrentImageUntilSelectedReady(position = this.previousGalleryPosition) {
      const hero = this.mediaGallery?.querySelector('[data-ltp-selected-variant-hero]');
      const heroMedia = hero?.querySelector('[data-ltp-selected-variant-hero-media]');
      const currentImage = this.mediaGallery?.querySelector(
        `[data-ltp-fixed-gallery] [data-ltp-gallery-position="${position}"] img`
      );
      if (!hero?.hidden || !heroMedia || !currentImage?.naturalWidth) return;
      const image = currentImage.cloneNode(true);
      if (this.mediaGallery.dataset.ltpMobileLifestyleFirst === 'true' &&
          !this.mediaGallery.mql.matches && Number(position) === 4 && currentImage.currentSrc) {
        image.removeAttribute('srcset');
        image.src = currentImage.currentSrc;
      }
      image.classList.add('ltp-selected-variant-hero__image');
      heroMedia.replaceChildren(image);
      hero.dataset.ltpVariantKey = '';
      hero.hidden = false;
      hero.setAttribute('aria-hidden', 'false');
      this.mediaGallery.querySelector('[data-ltp-fixed-gallery]')?.setAttribute('aria-hidden', 'true');
    }

    contextualImageUrl(source, width) {
      const url = new URL(source, window.location.href);
      url.searchParams.set('width', String(width));
      return url.toString();
    }

    updateContextualSlide(position, key, selected) {
      const gallery = this.mediaGallery;
      const slide = gallery?.querySelector(`[data-ltp-fixed-gallery] [data-ltp-gallery-position="${position}"]`);
      if (!slide || (slide.dataset.ltpVariantKey === key &&
          (!this.mobileEditorialGallery || slide.querySelector('.product__media img')?.naturalWidth))) return;

      const state = this.getVariantState(key);
      const source = state?.content.querySelector('[data-ltp-selected-hero-fragment] img');
      const existing = slide.querySelector('.product__media img');
      if (!source || !existing) return;

      gallery.deferredImageObserver?.unobserve(existing);
      const prepared = this.mobileEditorialGallery ? this.variantImagePreloads.get(key) : null;
      if (this.mobileEditorialGallery && !prepared?.decoded) return;
      const image = prepared ? prepared.image : existing.cloneNode(false);
      image.removeAttribute('data-ltp-deferred-image');
      image.removeAttribute('data-ltp-deferred-src');
      image.removeAttribute('data-ltp-deferred-srcset');
      if (!prepared) {
        image.removeAttribute('src');
        image.removeAttribute('srcset');
      }
      image.alt = source.alt;
      image.width = source.width;
      image.height = source.height;
      image.loading = selected ? 'eager' : 'lazy';
      image.fetchPriority = selected ? 'high' : 'low';
      image.decoding = 'async';
      if (!prepared) {
        image.srcset = source.getAttribute('srcset') || '';
        image.src = source.getAttribute('src') || '';
      }
      existing.replaceWith(image);
      slide.dataset.ltpVariantKey = key;
      if (this.mobileEditorialGallery) {
        gallery.querySelectorAll(`[data-ltp-loop-position="${position}"] .product__media img`).forEach((clone) => clone.replaceWith(image.cloneNode(false)));
      }

      const thumbnail = gallery.elements.thumbnails?.querySelector(`[data-ltp-gallery-position="${position}"] img`);
      if (thumbnail) {
        thumbnail.alt = source.alt;
        thumbnail.removeAttribute('srcset');
        thumbnail.src = this.contextualImageUrl(source.getAttribute('src'), 208);
      }

      const mediaId = state.dataset.mediaId;
      const opener = slide.querySelector('.product__media-toggle[data-media-id]');
      if (opener && mediaId) opener.dataset.mediaId = mediaId;
      const modalImage = document.querySelector(
        `#ProductModal-${this.productInfo.dataset.section} [data-ltp-modal-image]:nth-of-type(${position})`
      );
      if (modalImage && mediaId) {
        modalImage.removeAttribute('src');
        modalImage.removeAttribute('srcset');
        modalImage.dataset.mediaId = mediaId;
        modalImage.dataset.ltpDeferredSrc = this.contextualImageUrl(source.getAttribute('src'), 2200);
        modalImage.dataset.ltpDeferredSrcset = [550, 1100, 1445, 2200]
          .map((width) => `${this.contextualImageUrl(source.getAttribute('src'), width)} ${width}w`)
          .join(', ');
        modalImage.alt = source.alt;
      }
    }

    updateContextualSlides(selectedKey, printOnly, selectedPosition) {
      const keys = this.getContextVariantKeys();
      if (printOnly) {
        this.updateContextualSlide(1, selectedKey, true);
        return;
      }
      keys.forEach((key, index) => this.updateContextualSlide(index + 1, key, index + 1 === selectedPosition));
    }

    positionContextualGallery() {
      if (this.mobileEditorialGallery) return;
      const gallery = this.mediaGallery;
      const selectedFormat = this.getSelectedFormat();
      const selectedKey = this.getSelectedVariantKey();
      if (!gallery || !selectedFormat || !selectedKey) return;

      const printOnly = selectedFormat.dataset.ltpFormatValue === 'print-only';
      const selectedFinish = this.selectors?.querySelector('[data-ltp-frame-finish-value]:checked')?.dataset.ltpFrameFinishValue;
      const position = printOnly ? 1 : Math.max(1, canonicalFinishValues.indexOf(selectedFinish) + 1);
      this.holdCurrentImageUntilSelectedReady();
      this.prepareVariantKey(selectedKey, 'high');
      this.updateContextualSlides(selectedKey, printOnly, position);
      [2, 3].forEach((number) => {
        const slide = gallery.querySelector(`[data-ltp-fixed-gallery] [data-ltp-gallery-position="${number}"]`);
        const thumbnail = gallery.elements.thumbnails?.querySelector(`[data-ltp-gallery-position="${number}"]`);
        if (slide) {
          slide.hidden = printOnly;
          slide.style.display = printOnly ? 'none' : '';
        }
        if (thumbnail) {
          thumbnail.hidden = printOnly;
          thumbnail.style.display = printOnly ? 'none' : '';
        }
      });
      gallery.dataset.ltpSlideCount = printOnly ? '2' : '4';
      gallery.dataset.ltpContextualGallery = 'true';
      gallery.querySelector('.slider-counter--total')?.replaceChildren(document.createTextNode(printOnly ? '2' : '4'));
      gallery.elements.viewer.enableSliderLooping = true;
      gallery.elements.viewer.prevButton?.removeAttribute('disabled');
      gallery.elements.viewer.nextButton?.removeAttribute('disabled');

      const slide = gallery.querySelector(`[data-ltp-fixed-gallery] [data-ltp-gallery-position="${position}"]`);
      if (slide) {
        this.settingGalleryPosition = true;
        gallery.setActiveMedia(slide.dataset.mediaId, false, 0, true);
        this.settingGalleryPosition = false;
        this.previousGalleryPosition = position;
      }
      gallery.elements.viewer.resetPages?.();
      gallery.updateNavigationState();
    }

    updateProductState(state) {
      this.productInfo.dataset.ltpConfigurationComplete = 'true';

      const price = this.productInfo.querySelector(`#price-${this.productInfo.dataset.section}`);
      const priceFragment = state.content.querySelector('[data-ltp-price-fragment]');
      if (price && priceFragment) price.innerHTML = priceFragment.innerHTML;

      const sku = this.productInfo.querySelector(`#Sku-${this.productInfo.dataset.section}`);
      if (sku) {
        const accessibleLabel = sku.querySelector('.visually-hidden')?.cloneNode(true);
        sku.replaceChildren();
        if (accessibleLabel) sku.append(accessibleLabel);
        sku.append(document.createTextNode(state.dataset.sku || ''));
        sku.classList.toggle('visibility-hidden', !state.dataset.sku);
      }

      this.productInfo
        .querySelectorAll(
          `#product-form-${this.productInfo.dataset.section} input[name="id"], #product-form-installment-${this.productInfo.dataset.section} input[name="id"]`
        )
        .forEach((input) => {
          input.value = state.dataset.variantId;
          input.disabled = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

      const selectedVariantJson = this.selectors?.querySelector('[data-selected-variant]');
      const nextVariantJson = state.content.querySelector('[data-ltp-variant-json]');
      if (selectedVariantJson && nextVariantJson) selectedVariantJson.textContent = nextVariantJson.textContent;

      const submitButton = this.productInfo.querySelector(`[id^="ProductSubmitButton-${this.productInfo.dataset.section}"]`);
      const available = state.dataset.available === 'true';
      if (submitButton) submitButton.disabled = !available;

      const dynamicCheckout = this.productInfo.querySelector('[data-ltp-dynamic-checkout]');
      if (dynamicCheckout) dynamicCheckout.hidden = !available;

      const pickupAvailability = this.productInfo.querySelector('[data-ltp-pickup-availability]');
      if (pickupAvailability) {
        pickupAvailability.dataset.variantId = state.dataset.variantId;
        pickupAvailability.hidden = true;
      }
    }

    setIncompleteState({ priceState = null, heroState = null, showVariantHero = false } = {}) {
      this.productInfo.dataset.ltpConfigurationComplete = 'false';
      if (showVariantHero && heroState) {
        this.showSelectedVariantHero(heroState);
      } else {
        this.exitSelectedVariantHero();
      }

      const price = this.productInfo.querySelector(`#price-${this.productInfo.dataset.section}`);
      const incompleteState = this.productInfo.querySelector('template[data-ltp-incomplete-state]');
      const priceFragment = (priceState || incompleteState)?.content.querySelector('[data-ltp-price-fragment]');
      if (price && priceFragment) price.innerHTML = priceFragment.innerHTML;

      const sku = this.productInfo.querySelector(`#Sku-${this.productInfo.dataset.section}`);
      if (sku) {
        const accessibleLabel = sku.querySelector('.visually-hidden')?.cloneNode(true);
        sku.replaceChildren();
        if (accessibleLabel) sku.append(accessibleLabel);
        sku.classList.add('visibility-hidden');
      }

      this.productInfo
        .querySelectorAll(
          `#product-form-${this.productInfo.dataset.section} input[name="id"], #product-form-installment-${this.productInfo.dataset.section} input[name="id"]`
        )
        .forEach((input) => {
          input.value = '';
          input.disabled = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

      const selectedVariantJson = this.selectors?.querySelector('[data-selected-variant]');
      if (selectedVariantJson) selectedVariantJson.textContent = 'null';

      const submitButton = this.productInfo.querySelector(`[id^="ProductSubmitButton-${this.productInfo.dataset.section}"]`);
      if (submitButton) submitButton.disabled = true;

      const dynamicCheckout = this.productInfo.querySelector('[data-ltp-dynamic-checkout]');
      if (dynamicCheckout) dynamicCheckout.hidden = true;

      const pickupAvailability = this.productInfo.querySelector('[data-ltp-pickup-availability]');
      if (pickupAvailability) {
        pickupAvailability.hidden = true;
        pickupAvailability.removeAttribute('available');
        pickupAvailability.dataset.variantId = '';
      }
    }

    onGalleryPositionChange(event) {
      if (this.settingGalleryPosition) return;
      if (event.detail.currentElement?.hidden) return;
      const position = Number(event.detail.currentElement?.dataset.ltpGalleryPosition);
      if (position !== 4 && this.mediaGallery) delete this.mediaGallery.dataset.ltpMobileOpening;
      if (!this.contextualGalleryActive) {
        this.previousGalleryPosition = position;
        this.exitSelectedVariantHero();
        return;
      }

      if (position === 4) {
        this.previousGalleryPosition = position;
        this.exitSelectedVariantHero();
        return;
      }

      const printOnly = this.getSelectedFormat()?.dataset.ltpFormatValue === 'print-only';
      if (!printOnly) {
        const finish = canonicalFinishValues[position - 1];
        const input = this.selectors?.querySelector(`[data-ltp-frame-finish-value="${finish}"]`);
        if (!input || input.disabled) return;
        input.checked = true;
        this.lastFrameFinish = finish;
        this.updateSelectedLabels();
      }

      const state = this.getVariantState();
      if (!state) return;
      const targetImage = event.detail.currentElement.querySelector('.product__media img');
      if (this.mobileEditorialGallery || (targetImage?.complete && targetImage.naturalWidth)) {
        this.exitSelectedVariantHero();
      } else {
        this.holdCurrentImageUntilSelectedReady();
        this.showSelectedVariantHero(state);
      }
      this.previousGalleryPosition = position;
      this.updateProductState(state);
      this.prewarmSelectedContext();
      if (this.mobileEditorialGallery) this.prepareMobileContext(state, false);
    }

    refreshSelectorState({ showVariantHero = null, deferPredictions = false } = {}) {
      const selectors = this.selectors;
      if (!selectors) return;

      this.resolveMissingSelections();
      const selectedFinish = selectors.querySelector('[data-ltp-frame-finish-value]:checked');
      if (selectedFinish) this.lastFrameFinish = selectedFinish.dataset.ltpFrameFinishValue;

      const format = this.getSelectedFormat();
      this.mediaGallery?.setMobileIndicatorCount?.(format?.dataset.ltpFormatValue === 'print-only');
      this.setFrameFinishVisibility(format);
      this.setSizeAvailability();
      this.updateSelectedLabels();
      this.syncBlankwallDimensions();

      const state = this.getVariantState();
      if (state) {
        if (showVariantHero === true) this.showSelectedVariantHero(state);
        if (showVariantHero === false) this.exitSelectedVariantHero();
        this.updateProductState(state);
        this.prewarmSelectedContext({ deferUntilSelectedReady: deferPredictions });
      } else {
        this.setIncompleteState({
          priceState: this.getFormatState(),
          heroState: this.getFormatPreviewState(),
          showVariantHero: showVariantHero === true,
        });
      }
    }
  }

  class LtpFrameGallerySync {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.mediaGallery = productInfo.querySelector('media-gallery');
      this.viewer = this.mediaGallery?.querySelector('[id^="GalleryViewer"]');
      if (!this.mediaGallery || !this.viewer) return;

      this.galleryDrivenMediaId = null;
      this.pendingSwatchMediaIds = new Set();
      this.frameTransitionToken = 0;
      this.updateMedia = productInfo.updateMedia?.bind(productInfo);
      if (this.updateMedia) {
        productInfo.updateMedia = this.updateProductMedia.bind(this);
      }

      this.annotateGallery();
      this.productInfo.addEventListener('change', this.onFrameInputChange.bind(this), true);
      this.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      this.activeMediaObserver = new MutationObserver((mutations) => {
        const activeMediaChanged = mutations.some(({ target, oldValue }) =>
          target.matches('[data-media-id]') &&
          (oldValue || '').split(/\s+/).includes('is-active') !== target.classList.contains('is-active')
        );
        if (activeMediaChanged) this.synchroniseActiveMedia();
      });
      this.activeMediaObserver.observe(this.mediaGallery.querySelector('[id^="Slider-Gallery"]'), {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true,
        subtree: true,
      });
      this.orderGallery();
      this.scheduleAlternateFramePrewarm();
      if (!window.matchMedia('(min-width: 990px)').matches) this.scheduleFirstLifestylePrewarm();
    }

    getFrameMediaId(frameValue) {
      const key = frameValue.charAt(0).toUpperCase() + frameValue.slice(1);
      return this.mediaGallery.dataset[`ltp${key}MediaId`];
    }

    onFrameInputChange(event) {
      const frameValue = event.target?.dataset?.ltpFrameValue;
      if (!frameValues.has(frameValue)) return;

      const mediaId = this.getFrameMediaId(frameValue);
      if (!mediaId || mediaId === this.galleryDrivenMediaId) return;

      this.pendingSwatchMediaIds.add(mediaId);
      this.transitionToFrameMedia(mediaId);
    }

    transitionToFrameMedia(mediaId) {
      const activeMedia = this.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia || activeMedia.classList.contains('is-active')) return;

      const transitionToken = ++this.frameTransitionToken;
      const image = activeMedia.querySelector('.product__modal-opener--image img');
      if (image && !(image.complete && image.naturalWidth)) image.loading = 'eager';

      const startTransition = () => {
        if (transitionToken !== this.frameTransitionToken) return;
        const startImmediately = !this.mediaGallery.mql.matches;
        this.mediaGallery.setActiveMedia(mediaId, false, 240, startImmediately);
      };

      if (!this.mediaGallery.mql.matches && image?.complete && image.naturalWidth) {
        startTransition();
        return;
      }

      const imageReady = image?.decode ? image.decode().catch(() => undefined) : Promise.resolve();
      imageReady.then(startTransition);
    }

    scheduleAlternateFramePrewarm() {
      if (this.alternatesPrewarmQueued || !window.matchMedia('(min-width: 990px)').matches) return;
      this.alternatesPrewarmQueued = true;

      const selectedImage = this.viewer.querySelector('[data-media-id].is-active img');
      const queue = () => {
        const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 250));
        schedule(() => this.prewarmAlternateFrames(), { timeout: 1500 });
      };

      if (selectedImage?.complete && selectedImage.naturalWidth) {
        selectedImage.decode?.().catch(() => undefined).finally(queue) || queue();
      } else if (selectedImage) {
        selectedImage.addEventListener('load', queue, { once: true });
      }
    }

    prewarmAlternateFrames() {
      if (!window.matchMedia('(min-width: 990px)').matches) return;

      const selectedMediaId = this.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
      this.prewarmedFrameImages = this.prewarmedFrameImages || [];
      frameValues.forEach((frameValue) => {
        const mediaId = this.getFrameMediaId(frameValue);
        if (!mediaId || mediaId === selectedMediaId) return;

        const sourceImage = this.viewer.querySelector(`[data-media-id="${mediaId}"] img`);
        if (!sourceImage || (sourceImage.complete && sourceImage.naturalWidth)) return;

        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.fetchPriority = 'low';
        preloader.sizes = sourceImage.sizes;
        preloader.srcset = sourceImage.srcset;
        preloader.src = sourceImage.src;
        this.prewarmedFrameImages.push(preloader);
      });
      this.scheduleFirstLifestylePrewarm();
    }

    getLifestyleMedia() {
      return Array.from(this.viewer.querySelectorAll('[data-media-id][data-ltp-gallery-order]'))
        .filter((media) => !media.dataset.ltpFrameValue && media.querySelector('img[data-ltp-deferred-image]'))
        .sort(
          (first, second) => Number(first.dataset.ltpGalleryOrder) - Number(second.dataset.ltpGalleryOrder)
        );
    }

    scheduleFirstLifestylePrewarm() {
      if (this.firstLifestylePrewarmQueued) return;
      this.firstLifestylePrewarmQueued = true;

      const firstLifestyle = this.getLifestyleMedia()[0];
      if (!firstLifestyle) return;

      const selectedImage = this.viewer.querySelector('[data-media-id].is-active img');
      const queue = () => {
        const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 250));
        schedule(() => this.mediaGallery.prepareMediaImage?.(firstLifestyle, 'low'), { timeout: 2000 });
      };

      if (selectedImage?.complete && selectedImage.naturalWidth) {
        selectedImage.decode?.().catch(() => undefined).finally(queue) || queue();
      } else if (selectedImage) {
        selectedImage.addEventListener('load', queue, { once: true });
      }
    }

    prepareNextLifestyle(activeMedia) {
      if (!activeMedia) return;

      const lifestyleMedia = this.getLifestyleMedia();
      let nextLifestyle = null;
      if (activeMedia.dataset.ltpFrameValue === 'black') {
        nextLifestyle = lifestyleMedia[0];
      } else if (!activeMedia.dataset.ltpFrameValue) {
        const activeIndex = lifestyleMedia.indexOf(activeMedia);
        if (activeIndex >= 0) nextLifestyle = lifestyleMedia[activeIndex + 1];
      }

      if (nextLifestyle) this.mediaGallery.prepareMediaImage?.(nextLifestyle, 'low');
    }

    onSlideChanged(event) {
      // resetPages also emits slideChanged while native media is being reordered.
      if (this.reconcilingGallery) return;
      if (!this.mediaGallery.mql.matches && this.mediaGallery.galleryAnimation) return;
      const currentMedia = event.detail?.currentElement;
      if (!currentMedia) return;

      // Touch scrolling updates the slider page, but Dawn leaves .is-active behind.
      // Keep that marker current without calling setActiveMedia or moving the slider.
      if (!this.mediaGallery.mql.matches) {
        this.viewer.querySelectorAll('[data-media-id]').forEach((media) => {
          if (media.classList.contains('is-active') !== (media === currentMedia)) {
            media.classList.toggle('is-active', media === currentMedia);
          }
        });
      }
      this.synchroniseFrame(currentMedia);
      this.prepareNextLifestyle(currentMedia);
    }

    synchroniseActiveMedia() {
      const activeMedia = this.mediaGallery.querySelector('[data-media-id].is-active');
      this.synchroniseFrame(activeMedia);
      this.prepareNextLifestyle(activeMedia);
    }

    synchroniseFrame(activeMedia) {
      const frameValue = activeMedia?.dataset?.ltpFrameValue;
      if (!frameValues.has(frameValue)) return;

      const input = this.productInfo.querySelector(
        `variant-selects input[data-ltp-frame-value="${frameValue}"]`
      );
      if (!input || input.checked) return;

      this.galleryDrivenMediaId = activeMedia.dataset.mediaId;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      const selectedValue = input.closest('.product-form__input')?.querySelector('[data-selected-value]');
      if (selectedValue) selectedValue.textContent = input.value.charAt(0).toUpperCase() + input.value.slice(1);
    }

    updateProductMedia(html, variantFeaturedMediaId) {
      const requestedMediaId = variantFeaturedMediaId
        ? `${this.productInfo.dataset.section}-${variantFeaturedMediaId}`
        : null;

      if (requestedMediaId && requestedMediaId === this.galleryDrivenMediaId) {
        this.galleryDrivenMediaId = null;
        return;
      }

      if (requestedMediaId && this.pendingSwatchMediaIds.has(requestedMediaId)) {
        this.pendingSwatchMediaIds.delete(requestedMediaId);
        return;
      }

      if (requestedMediaId === this.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId) return;

      this.galleryDrivenMediaId = null;
      this.reconcilingGallery = true;
      try {
        return this.updateMedia(html, variantFeaturedMediaId);
      } finally {
        this.reconcilingGallery = false;
      }
    }

    orderGallery() {
      this.annotateGallery();
      const activeMedia = this.mediaGallery.querySelector('[data-media-id].is-active');
      const reordered = this.sortChildren(
        this.mediaGallery.querySelector('[id^="Slider-Gallery"]'),
        '[data-ltp-gallery-order]'
      );

      const thumbnailList = this.mediaGallery.querySelector('[id^="Slider-Thumbnails"]');
      this.sortChildren(thumbnailList, '[data-ltp-gallery-order]');
      thumbnailList
        ?.querySelectorAll('[data-target]')
        .forEach((thumbnail, index) => {
          thumbnail.dataset.mediaPosition = index + 1;
        });

      this.mediaGallery.updateNavigationState?.();

      // A gallery-driven variant response must not restart a settled or ongoing swipe.
      if (!reordered) return;
      this.reconcilingGallery = true;
      try {
        this.viewer.resetPages?.();
      } finally {
        this.reconcilingGallery = false;
      }

      if (!activeMedia) return;
      requestAnimationFrame(() => {
        this.viewer.slider?.scrollTo({ left: activeMedia.offsetLeft });
      });
    }

    annotateGallery() {
      const mediaIds = {
        black: this.mediaGallery.dataset.ltpBlackMediaId,
        white: this.mediaGallery.dataset.ltpWhiteMediaId,
        oak: this.mediaGallery.dataset.ltpOakMediaId,
      };
      const mediaOrder = { white: 0, oak: 1, black: 2 };

      [
        ['[id^="Slider-Gallery"] > [data-media-id]', 'mediaId'],
        ['[id^="Slider-Thumbnails"] > [data-target]', 'target'],
      ].forEach(([selector, idKey]) => {
        let lifestyleOrder = 3;
        this.mediaGallery.querySelectorAll(selector).forEach((item) => {
          const frameValue = [...frameValues].find((value) => mediaIds[value] === item.dataset[idKey]);
          if (frameValue) {
            item.dataset.ltpFrameValue = frameValue;
            item.dataset.ltpGalleryOrder = mediaOrder[frameValue];
          } else {
            delete item.dataset.ltpFrameValue;
            item.dataset.ltpGalleryOrder = lifestyleOrder;
            lifestyleOrder += 1;
          }
        });
      });
    }

    sortChildren(container, selector) {
      if (!container) return false;
      const items = Array.from(container.querySelectorAll(`:scope > ${selector}`));
      const sortedItems = [...items].sort(
        (first, second) => Number(first.dataset.ltpGalleryOrder) - Number(second.dataset.ltpGalleryOrder)
      );
      if (items.every((item, index) => item === sortedItems[index])) return false;
      sortedItems.forEach((item) => container.append(item));
      return true;
    }
  }

  const initialise = (root = document) => {
    const productInfos = root.matches?.('product-info')
      ? [root]
      : root.querySelectorAll('product-info');

    productInfos.forEach((productInfo) => {
      if (productInfo.dataset.ltpFuturePdp === 'true') {
        if (!productInfo.ltpCanonicalPdp) {
          productInfo.ltpCanonicalPdp = new LtpCanonicalPdp(productInfo);
        } else {
          productInfo.ltpCanonicalPdp.refreshSelectorState();
        }
        return;
      }

      if (productInfo.ltpFrameGallerySync) return;
      const mediaGallery = productInfo.querySelector('media-gallery');
      const galleryValues = [
        mediaGallery?.dataset.ltpBlackMediaId,
        mediaGallery?.dataset.ltpWhiteMediaId,
        mediaGallery?.dataset.ltpOakMediaId,
      ];
      const inputValues = Array.from(productInfo.querySelectorAll('input[data-ltp-frame-value]')).map(
        (input) => input.dataset.ltpFrameValue
      );
      const hasCompleteMapping =
        galleryValues.every(Boolean) && new Set(galleryValues).size === 3 && [...frameValues].every((value) => inputValues.includes(value));
      if (!hasCompleteMapping) return;

      productInfo.setAttribute('data-ltp-frame-sync', '');
      productInfo.ltpFrameGallerySync = new LtpFrameGallerySync(productInfo);
    });
  };

  document.addEventListener('product-info:loaded', (event) => initialise(event.target));
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.getElementById(`MainProduct-${data.sectionId}`);
      if (productInfo?.ltpCanonicalPdp) {
        productInfo.ltpCanonicalPdp.refreshSelectorState();
        return;
      }
      productInfo?.ltpFrameGallerySync?.orderGallery();
    });
  }

  initialise();
})();
